import Pagination from '../../components/Common/Pagination';
import { TableLoader } from '../../components/Common/Loader';
import React, { useState, useMemo } from 'react';
import { ArrowLeft } from 'lucide-react';
import CSVButton from '../../components/Common/CSVButton';
import { exportToCSV } from '../../utils/csvExport';

interface Variant {
  id: number;
  name: string;
  erpId: string;
  status: 'Active' | 'Inactive';
}

const mockVariants: Variant[] = [
  { id: 1, name: 'L', erpId: '-', status: 'Active' },
  { id: 2, name: 'KG', erpId: '-', status: 'Active' },
  { id: 3, name: 'BOX', erpId: '-', status: 'Active' },
  { id: 4, name: 'CUP', erpId: '-', status: 'Active' },
  { id: 5, name: 'BKT', erpId: '-', status: 'Active' },
  { id: 6, name: 'NOS', erpId: '-', status: 'Active' },
  { id: 7, name: '2Kg', erpId: '-', status: 'Active' }
];

export const VariantsList: React.FC = () => {
  const [variants, setVariants] = useState<Variant[]>(mockVariants);
  const [loading, _setLoading] = useState<boolean>(false);
  const [view, setView] = useState<'list' | 'add' | 'edit'>('list');
  const [editingVariant, setEditingVariant] = useState<Variant | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [entriesLimit, setEntriesLimit] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    erpId: ''
  });

  // Filtering Logic
  const filteredVariants = useMemo(() => {
    return variants.filter((variant) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          variant.name.toLowerCase().includes(query) ||
          variant.erpId.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [variants, searchQuery]);

  // Paginated Variants
  const paginatedVariants = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesLimit;
    return filteredVariants.slice(startIndex, startIndex + entriesLimit);
  }, [filteredVariants, currentPage, entriesLimit]);

  const handleOpenAdd = () => {
    setEditingVariant(null);
    setFormData({ name: '', erpId: '' });
    setView('add');
  };

  const handleOpenEdit = (variant: Variant) => {
    setEditingVariant(variant);
    setFormData({
      name: variant.name,
      erpId: variant.erpId === '-' ? '' : variant.erpId
    });
    setView('edit');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      alert('Category Name is required.');
      return;
    }

    if (editingVariant) {
      // Edit
      setVariants(variants.map(v => v.id === editingVariant.id ? {
        ...v,
        name: formData.name,
        erpId: formData.erpId || '-',
      } : v));
    } else {
      // Add
      const newVar: Variant = {
        id: variants.length > 0 ? Math.max(...variants.map(v => v.id)) + 1 : 1,
        name: formData.name,
        erpId: formData.erpId || '-',
        status: 'Active',
      };
      setVariants([...variants, newVar]);
    }

    setView('list');
  };

  if (view === 'add' || view === 'edit') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button 
            type="button"
            onClick={() => setView('list')}
            className="w-7 h-7 rounded-full go_erp-bg transition-all flex items-center justify-center text-white cursor-pointer" title="Go Back"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h3 className="text-xl font-bold go_erp-text tracking-wide">
            {view === 'edit' ? 'Edit Variant' : 'Add Variant'}
          </h3>
        </div>
        </div>

        {/* Form Container */}
        <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {/* Category Name */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Varient Name*</label>
              <input 
                type="text" 
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full border border-[#828282] rounded-xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800" 
              />
            </div>

            {/* ERP ID */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">ERP/SAP ID</label>
              <input 
                type="text" 
                value={formData.erpId}
                onChange={(e) => setFormData({...formData, erpId: e.target.value})}
                className="w-full border border-[#828282] rounded-xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800" 
              />
            </div>
          </div>
        </form>
          {/* Submit Button */}
          <div className="flex justify-center">
            <button 
              type="submit" 
              className="bg-[#DCA10D] hover:bg-[#c28c00] text-white font-bold py-3 px-16 rounded-xl text-sm transition-all cursor-pointer shadow-md hover:shadow-[#DCA10D]/20"
            >
              Submit
            </button>
          </div>
      </div>
    );
  }

  const handleExportCSV = () => {
    const headers = ['Variant ID', 'Name', 'ERP ID', 'Status'];
    const rows = filteredVariants.map((v) => [
      v.id,
      v.name,
      v.erpId,
      v.status,
    ]);
    exportToCSV('variants_list', headers, rows);
  };

  return (
    <div className="space-y-6">
      {/* Control Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        {/* Title and Back */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
            title="Go Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
            Variant
          </h1>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleOpenAdd}
            className="go_erp-bg text-white text-sm px-4 py-2.5 rounded-xl flex items-center gap-2 cursor-pointer transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Variant
          </button>
          
          <CSVButton onClick={handleExportCSV} />
        </div>
      </div>

      {/* Table Main Card */}
      <div className="bg-white rounded-2xl overflow-hidden">
        
        {/* Table Toolbar */}
        <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500">Show</span>
            <select
              value={entriesLimit}
              onChange={(e) => setEntriesLimit(Number(e.target.value))}
              className="bg-white border border-slate-200 text-xs font-bold text-slate-700 rounded px-2 py-1 outline-none focus:border-slate-400"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-xs font-semibold text-slate-500">entries</span>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            {/* Table Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-white border border-slate-200 text-xs rounded-full py-1.5 pl-8 pr-3 outline-none focus:border-slate-400 w-44 transition-all"
              />
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
            </div>
          </div>
        </div>

        {/* Table Wrapper */}
        <div className="overflow-x-auto select-none">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#FFF8E1]">
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">ID</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Name</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">ERP/SAP ID</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Status</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#ECECF2]">
              {loading ? (
                <TableLoader colSpan={5} text="Loading..." />
              ) : paginatedVariants.length > 0 ? (
                paginatedVariants.map((variant) => (
                  <tr key={variant.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-4 text-xs text-[#828282]">{variant.id}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{variant.name}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{variant.erpId}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{variant.status}</td>
                    <td className="px-4 py-4 text-xs text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleOpenEdit(variant)} className="p-1.5 text-slate-500 hover:text-[#B71C1C] transition-colors cursor-pointer rounded-lg" title="View">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                          </button>
                          <button onClick={() => handleOpenEdit(variant)} className="p-1.5 text-slate-500 hover:text-blue-600 transition-colors cursor-pointer rounded-lg" title="Edit">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                          </button>
                        </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-xs font-semibold text-slate-400">
                    No variants found matching filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <Pagination
          currentPage={currentPage}
          totalEntries={filteredVariants.length}
          entriesLimit={entriesLimit}
          onPageChange={setCurrentPage}
        />

      </div>
    </div>
  );
};
export default VariantsList;
